// ═══════════════════════════════════════════════════════════
// Position Utilities
//
// Cross-category accessors and helpers that work on any Position.
// These eliminate the ~40 inline filter+reduce blocks scattered
// across Strategy, Dashboard, and Grid components.
// ═══════════════════════════════════════════════════════════

import type {
  Position,
  SupplyPosition,
  DemandPosition,
  HedgePosition,
} from '../sp-types/positions';
import { isSupply, isDemand, isHedge } from '../sp-types/positions';
import type { ExposureHorizonWindow } from './exposureHorizon';
import { isWithinHorizon } from './exposureHorizon';

// ─── Safe Numeric Coercion ──────────────────────────────────
// Replaces the ~25× repeated pattern:
//   typeof p.quantity === 'number' ? p.quantity : parseFloat(String(p.quantity)) || 0

export function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (value == null) return 0;
  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
}

// ─── Cross-Category Accessors ───────────────────────────────
// These let callers work with any Position without knowing
// which field name each category uses.

/** The date that defines when this position is relevant (YYYY-MM-DD) */
export function getPeriodDate(p: Position): string {
  if (isSupply(p)) return p.delivery_or_available_period;
  if (isDemand(p)) return p.delivery_period;
  return p.maturity_date;
}

/** The effective quantity of this position */
export function getQuantity(p: Position): number {
  if (isHedge(p)) return toNumber(p.hedge_effect ?? p.hedged_quantity_mt);
  return toNumber(p.quantity);
}

/** Absolute quantity (always positive) */
export function getAbsQuantity(p: Position): number {
  return Math.abs(getQuantity(p));
}

/** The position's unique ID (hedge uses hedge_position_id) */
export function getId(p: Position): number {
  if (isHedge(p)) return p.hedge_position_id;
  return p.id;
}

/** The subtype within the category */
export function getSubtype(p: Position): string {
  if (isSupply(p)) return p.supply_type;
  if (isDemand(p)) return p.demand_kind;
  return p.instrument_type;
}

/** Counterparty name (supplier / customer / broker) */
export function getCounterparty(p: Position): string | null {
  if (isSupply(p)) return p.supplier_name;
  if (isDemand(p)) return p.customer_name;
  return p.broker_name;
}

/** Reference identifier (PO # / order # / contract code) */
export function getReference(p: Position): string | null {
  if (isSupply(p)) return p.po_reference;
  if (isDemand(p)) return p.order_reference;
  return p.contract_code;
}

/** Unit price amount */
export function getUnitPrice(p: Position): number | null {
  if (isHedge(p)) return p.underlying_price;
  return p.unit_price_amount;
}

/** Unit price currency */
export function getCurrency(p: Position): string | null {
  if (isHedge(p)) return p.hedge_currency;
  return p.unit_price_currency;
}

// ─── Date Helpers ───────────────────────────────────────────

export interface MonthYear {
  year: number;
  month: number; // 1-12
}

/** Extract { year, month } from an ISO date string (YYYY-MM-DD) */
export function getMonthYear(dateStr: string): MonthYear {
  const [year, month] = dateStr.split('-').map(Number);
  return { year, month };
}

/** Check if a position falls in a specific month */
export function isInMonth(p: Position, year: number, month: number): boolean {
  const my = getMonthYear(getPeriodDate(p));
  return my.year === year && my.month === month;
}

/** Get a short month label like "Jan25" from an ISO date string */
export function toMonthLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[d.getMonth()] + String(d.getFullYear()).substring(2);
}

// ─── Filtering ──────────────────────────────────────────────

export interface FilterOptions {
  subtype?: string;
  excludeFromCalcs?: boolean; // if true, include items even if exclude_from_calcs is set
}

/** Filter positions by formula code */
export function filterByFormula<T extends Position>(positions: T[], formulaCode: string): T[] {
  return positions.filter(p => p.formula_code === formulaCode);
}

/** Filter positions by formula code and month (1-12) */
export function filterByFormulaAndMonth<T extends Position>(
  positions: T[],
  formulaCode: string,
  year: number,
  month: number,
  opts?: FilterOptions,
): T[] {
  return positions.filter(p => {
    if (p.formula_code !== formulaCode) return false;

    const my = getMonthYear(getPeriodDate(p));
    if (my.year !== year || my.month !== month) return false;

    if (opts?.subtype && getSubtype(p) !== opts.subtype) return false;

    // For supply: respect exclude_from_calcs unless caller opts out or specifies a subtype
    if (isSupply(p) && !opts?.excludeFromCalcs && !opts?.subtype && p.exclude_from_calcs) return false;

    return true;
  });
}

// ─── Aggregation ────────────────────────────────────────────

/** Sum the effective quantity of positions */
export function sumQuantity(positions: Position[]): number {
  return positions.reduce((sum, p) => sum + getQuantity(p), 0);
}

/** Filter by formula (all months), then sum quantity. */
export function sumByFormula(
  positions: Position[],
  formulaCode: string,
  opts?: FilterOptions,
): number {
  const filtered = positions.filter(p => {
    if (p.formula_code !== formulaCode) return false;
    if (opts?.subtype && getSubtype(p) !== opts.subtype) return false;
    if (isSupply(p) && !opts?.excludeFromCalcs && !opts?.subtype && p.exclude_from_calcs) return false;
    return true;
  });
  return sumQuantity(filtered);
}

/** Filter by formula + month, then sum quantity. The most common operation. */
export function sumByFormulaAndMonth(
  positions: Position[],
  formulaCode: string,
  year: number,
  month: number,
  opts?: FilterOptions,
): number {
  return sumQuantity(filterByFormulaAndMonth(positions, formulaCode, year, month, opts));
}

// ─── Grouping ───────────────────────────────────────────────

/** Group positions by month label (e.g., "Jan25") */
export function groupByMonth<T extends Position>(positions: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const p of positions) {
    const label = toMonthLabel(getPeriodDate(p));
    if (!groups[label]) groups[label] = [];
    groups[label].push(p);
  }
  return groups;
}

/** Group positions by formula code */
export function groupByFormula<T extends Position>(positions: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const p of positions) {
    const key = p.formula_code;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return groups;
}

/** Group positions by subtype */
export function groupBySubtype<T extends Position>(positions: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const p of positions) {
    const key = getSubtype(p);
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return groups;
}

/** Group positions by counterparty */
export function groupByCounterparty<T extends Position>(positions: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const p of positions) {
    const key = getCounterparty(p) || 'Unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return groups;
}

// ─── Horizon Filtering ──────────────────────────────────────

/**
 * Split positions into in-scope and out-of-scope based on horizon window.
 * Replaces the identical 15-line blocks copy-pasted in Inventory.tsx,
 * Demand.tsx, and FinancialPositions.tsx.
 */
export function splitByHorizon<T extends Position>(
  positions: T[],
  window: ExposureHorizonWindow,
): { inScope: T[]; outOfScope: T[] } {
  const inScope: T[] = [];
  const outOfScope: T[] = [];

  for (const p of positions) {
    const my = getMonthYear(getPeriodDate(p));
    if (isWithinHorizon(my, window)) {
      inScope.push(p);
    } else {
      outOfScope.push(p);
    }
  }

  return { inScope, outOfScope };
}

/**
 * Check if a single position is out-of-scope for the horizon.
 * Useful in grid row rendering to flag individual rows.
 */
export function isOutOfScope(p: Position, window: ExposureHorizonWindow): boolean {
  const my = getMonthYear(getPeriodDate(p));
  return !isWithinHorizon(my, window);
}

/** Check if a supply position is a ratio PO (no fixed price, excluded from calcs) */
export function isRatioPO(p: SupplyPosition): boolean {
  return p.supply_type === 'ratio_po';
}

// ─── Extraction ─────────────────────────────────────────────

/** Get unique formula codes from a list of positions */
export function getUniqueFormulaCodes(positions: Position[]): string[] {
  return Array.from(new Set(positions.map(p => p.formula_code)));
}

// ─── Chart Aggregation ──────────────────────────────────────

/**
 * Dictionary shape used by summary charts (bar + donut) on category pages.
 * Keyed by formula_code, each entry has monthly value breakdown + totals.
 */
export interface MaterialExposureDictionary {
  totalValue: number;
  totalWeight: number;
  exposureCalendar: Record<string, number>; // key = `${year}${month}` e.g. "20263"
  price?: number;
}

/**
 * Aggregate positions into a per-formula dictionary for chart rendering.
 * Replaces the nearly-identical `aggregateSupplyPositionsToMaterialsDic`
 * and `aggregateDemandPositionsToMaterialsDic` functions.
 *
 * @param positions  Any position array (supply, demand, or hedge)
 * @param priceDic   formula_code → spot price (in base currency)
 * @param selMaterial  Optional formula_code filter (omit or undefined = all)
 */
export function aggregateToMaterialsDic<T extends Position>(
  positions: T[],
  priceDic: Record<string, number>,
  selMaterial?: string,
): Record<string, MaterialExposureDictionary> {
  const result: Record<string, MaterialExposureDictionary> = {};

  for (const p of positions) {
    const code = p.formula_code;
    if (selMaterial && selMaterial !== code) continue;

    const my = getMonthYear(getPeriodDate(p));
    const qty = getQuantity(p);
    const price = priceDic[code] || 0;
    const value = qty * price;
    const dateKey = `${my.year}${my.month}`;

    if (!result[code]) {
      result[code] = { exposureCalendar: {}, totalValue: 0, totalWeight: 0 };
    }

    result[code].exposureCalendar[dateKey] =
      (result[code].exposureCalendar[dateKey] || 0) + value;
    result[code].totalValue += value;
    result[code].totalWeight += qty;
  }

  return result;
}

/**
 * Simple per-formula totals (quantity + value). Used for summary cards.
 */
export function aggregateByFormula<T extends Position>(
  positions: T[],
  priceDic: Record<string, number>,
): Record<string, { quantity: number; totalValue: number }> {
  const result: Record<string, { quantity: number; totalValue: number }> = {};

  for (const p of positions) {
    const code = p.formula_code;
    if (!result[code]) result[code] = { quantity: 0, totalValue: 0 };

    const qty = getQuantity(p);
    result[code].quantity += qty;
    result[code].totalValue += qty * (priceDic[code] || 0);
  }

  return result;
}

// ─── Usage Examples ─────────────────────────────────────────
//
// On-hand stock (replaces calculateCurrentStock):
//   sumByFormula(supplyPositions, 'CZPR', { subtype: 'on_hand_inventory' })
//
// POs for a month:
//   sumByFormulaAndMonth(supplyPositions, 'CZPR', 2026, 3, { subtype: 'purchase_order' })
//
// Demand for a month:
//   sumByFormulaAndMonth(demandPositions, 'CZPR', 2026, 3)
//
// Hedge effect for a month:
//   sumByFormulaAndMonth(hedgePositions, 'CZPR', 2026, 3)
